// src/contexts/CacheContext.tsx - Sistema de caché global optimizado para rendimiento

import React, { createContext, useContext, useCallback, useEffect, useRef, useMemo } from 'react';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

interface CacheContextType {
  get: <T>(key: string) => T | null;
  set: <T>(key: string, data: T, ttlMinutes?: number) => void;
  has: (key: string) => boolean;
  invalidate: (key: string) => void;
  invalidatePattern: (pattern: string) => void;
  clear: (pattern?: string) => void;
}

const CacheContext = createContext<CacheContextType | undefined>(undefined);

const CACHE_PREFIX = 'football_predictions_cache_';
const DEFAULT_TTL_MINUTES = 10; // 10 minutos por defecto

export const CacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Usar useRef para evitar re-renders cuando el caché cambia
  const memoryCacheRef = useRef<Map<string, CacheEntry<any>>>(new Map());
  const isInitializedRef = useRef(false);

  // Cargar caché desde localStorage al iniciar (solo una vez)
  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const loadFromLocalStorage = () => {
      const now = Date.now();

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(CACHE_PREFIX)) {
          try {
            const item = localStorage.getItem(key);
            if (item) {
              const entry: CacheEntry<any> = JSON.parse(item);

              // Solo cargar si no ha expirado
              if (entry.expiresAt > now) {
                const cacheKey = key.replace(CACHE_PREFIX, '');
                memoryCacheRef.current.set(cacheKey, entry);
              } else {
                // Limpiar entradas expiradas
                localStorage.removeItem(key);
              }
            }
          } catch (error) {
            localStorage.removeItem(key);
          }
        }
      }
    };

    loadFromLocalStorage();
  }, []);

  // Obtener dato del caché - NO depende de estado, no causa re-renders
  const get = useCallback(<T,>(key: string): T | null => {
    const now = Date.now();

    // Buscar en memoria primero
    const entry = memoryCacheRef.current.get(key);
    if (entry) {
      if (entry.expiresAt > now) {
        return entry.data as T;
      } else {
        // Limpiar entrada expirada
        memoryCacheRef.current.delete(key);
        try {
          localStorage.removeItem(CACHE_PREFIX + key);
        } catch (e) { /* ignore */ }
        return null;
      }
    }

    // Si no está en memoria, buscar en localStorage
    try {
      const item = localStorage.getItem(CACHE_PREFIX + key);
      if (item) {
        const entry: CacheEntry<T> = JSON.parse(item);

        if (entry.expiresAt > now) {
          // Restaurar a memoria (sin causar re-render)
          memoryCacheRef.current.set(key, entry);
          return entry.data;
        } else {
          // Limpiar entrada expirada
          localStorage.removeItem(CACHE_PREFIX + key);
          return null;
        }
      }
    } catch (error) {
      // silently ignore
    }

    return null;
  }, []);

  // Guardar dato en el caché - NO causa re-renders
  const set = useCallback(<T,>(key: string, data: T, ttlMinutes: number = DEFAULT_TTL_MINUTES): void => {
    const now = Date.now();
    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      expiresAt: now + (ttlMinutes * 60 * 1000)
    };

    // Guardar en memoria (sin setState)
    memoryCacheRef.current.set(key, entry);

    // Guardar en localStorage para persistencia
    try {
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
    } catch (error) {
      // Si localStorage está lleno, limpiar entradas antiguas
      if (error instanceof DOMException && error.code === 22) {
        clearOldEntries();
        try {
          localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
        } catch (retryError) {
          // silently ignore
        }
      }
    }
  }, []);

  // Verificar si existe en caché
  const has = useCallback((key: string): boolean => {
    return get(key) !== null;
  }, [get]);

  // Invalidar una entrada específica
  const invalidate = useCallback((key: string): void => {
    memoryCacheRef.current.delete(key);

    try {
      localStorage.removeItem(CACHE_PREFIX + key);
    } catch (error) {
      // silently ignore
    }
  }, []);

  // Invalidar por patrón (ej: "predictions_*")
  const invalidatePattern = useCallback((pattern: string): void => {
    const regex = new RegExp(pattern.replace('*', '.*'));

    // Limpiar memoria
    Array.from(memoryCacheRef.current.keys()).forEach(key => {
      if (regex.test(key)) {
        memoryCacheRef.current.delete(key);
      }
    });

    // Limpiar localStorage
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(CACHE_PREFIX)) {
          const cacheKey = key.replace(CACHE_PREFIX, '');
          if (regex.test(cacheKey)) {
            keysToRemove.push(key);
          }
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      // silently ignore
    }
  }, []);

  // Limpiar todo el caché o por patrón
  const clear = useCallback((pattern?: string): void => {
    if (pattern) {
      // Limpiar solo entradas que coincidan con el patrón
      invalidatePattern(pattern + '*');
      return;
    }

    memoryCacheRef.current.clear();

    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(CACHE_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      // silently ignore
    }
  }, [invalidatePattern]);

  // Limpiar entradas antiguas de localStorage
  const clearOldEntries = useCallback((): void => {
    try {
      const entries: Array<{ key: string; timestamp: number }> = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(CACHE_PREFIX)) {
          try {
            const item = localStorage.getItem(key);
            if (item) {
              const entry: CacheEntry<any> = JSON.parse(item);
              entries.push({ key, timestamp: entry.timestamp });
            }
          } catch {
            // Si falla el parse, agregar con timestamp 0 para eliminarlo
            entries.push({ key, timestamp: 0 });
          }
        }
      }

      // Ordenar por timestamp (más antiguo primero)
      entries.sort((a, b) => a.timestamp - b.timestamp);

      // Eliminar el 30% más antiguo
      const toRemove = Math.ceil(entries.length * 0.3);
      entries.slice(0, toRemove).forEach(({ key }) => {
        localStorage.removeItem(key);
        // También limpiar de memoria
        const cacheKey = key.replace(CACHE_PREFIX, '');
        memoryCacheRef.current.delete(cacheKey);
      });
    } catch (error) {
      // silently ignore
    }
  }, []);

  // Memoizar el valor del contexto para evitar re-renders
  const value = useMemo<CacheContextType>(() => ({
    get,
    set,
    has,
    invalidate,
    invalidatePattern,
    clear
  }), [get, set, has, invalidate, invalidatePattern, clear]);

  return (
    <CacheContext.Provider value={value}>
      {children}
    </CacheContext.Provider>
  );
};

export const useCache = (): CacheContextType => {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within a CacheProvider');
  }
  return context;
};
